import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type { SubscriptionAccessChange } from 'lumen/components/settings/users/modals/modal-subscription-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { SubscriptionAccessModel } from 'lumen/routes/hotel/settings/users/subscription-access';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

interface SortState {
  isAscending: boolean;
  valuePath: string;
}

export interface BulkDeletePayload {
  exclude: boolean;
  q?: string;
  subscription_ids: number[];
  user_id: string;
}

export default class HotelSettingsUsersSubscriptionAccessController extends Controller {
  @tracked declare model: SubscriptionAccessModel;

  queryParams = ['userId', 'userEmail'];

  @service('ajax') declare ajaxService: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked currentPage = 0;
  @tracked relationships: Userhotelrelationship[] = [];
  @tracked searchQuery: string | null = null;
  @tracked searchText = '';
  @tracked sortBy: string | null = null;
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked totalResults = 0;
  userEmail = '';
  userId = '';

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();

    this.searchQuery = trimmed || null;

    await this.reload();
  });

  loadMoreTask = task({ drop: true }, async () => {
    if (!this.hasMore) {
      return;
    }

    await this.loadPage(this.currentPage + 1);
  });

  get hasMore(): boolean {
    return this.relationships.length < this.totalResults;
  }

  get isLoading(): boolean {
    // Paging reports through isLoadingMore: swapping the table body for the
    // loading skeleton collapses the scroll container and jumps to the top.
    if (this.isLoadingMore) {
      return false;
    }

    return this.model.loadRelationshipsTask.isRunning;
  }

  get isLoadingMore(): boolean {
    return this.loadMoreTask.isRunning;
  }

  get isOwnUser(): boolean {
    return this.userId === this.hotelAndMarketService.user?.id;
  }

  get sorts(): SortState[] {
    if (!this.sortBy) {
      return [];
    }

    return [
      {
        valuePath: this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  private async loadPage(page: number): Promise<void> {
    const result = await this.model.loadRelationshipsTask.perform({
      page,
      searchQuery: this.searchQuery ?? undefined,
      sortBy: this.sortBy ?? undefined,
      sortDirection: this.sortDirection,
      userId: this.userId,
    });

    if (!result) {
      this.totalResults = this.relationships.length;
      return;
    }

    this.currentPage = page;
    this.totalResults = result.totalResults;
    this.relationships =
      page === 0
        ? result.relationships
        : [...this.relationships, ...result.relationships];
  }

  private async reload(): Promise<void> {
    this.currentPage = 0;
    this.relationships = [];

    try {
      await this.loadPage(0);
    } catch (error) {
      // The load task is restartable, so it gets canceled whenever a new load
      // starts or the route is torn down. Only real errors are worth re-throwing.
      if (!didCancel(error)) {
        throw error;
      }
    }
  }

  async resetAndLoad(): Promise<void> {
    this.searchText = '';
    this.searchQuery = null;
    this.sortBy = null;
    this.sortDirection = 'asc';
    this.totalResults = 0;

    await this.reload();
  }

  @action
  async addSubscriptionAccess(subscriptionIds: number[]): Promise<void> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'POST',
        data: {
          subscription_ids: subscriptionIds,
          user_id: this.userId,
        },
      });

      this.toast.success(
        this.intl.t('settings.users.add_subscription.success'),
        { showIcon: true },
      );

      await this.reload();
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.add_subscription.failure'), {
        showIcon: true,
      });
    }
  }

  @action
  async bulkDelete(payload: BulkDeletePayload): Promise<boolean> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: payload,
      });

      this.toast.success(this.intl.t('settings.users.bulk.delete_success'), {
        showIcon: true,
      });

      this.router.refresh('hotel.settings.users.subscription-access');
      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk.delete_failure'), {
        showIcon: true,
      });

      this.router.refresh('hotel.settings.users.subscription-access');
      return false;
    }
  }

  @action
  close(): void {
    this.router.transitionTo('hotel.settings.users');
  }

  @action
  async deleteRelationship(
    subscriptionId: number,
    subscriptionName: string,
  ): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('settings.users.subscription_access.confirm_delete', {
        userName: this.userEmail,
        subscriptionName,
      }),
      title: this.intl.t(
        'settings.users.subscription_access.remove_subscription',
      ),
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: {
          subscription_ids: [subscriptionId],
          user_id: this.userId,
        },
      });

      const record = (
        this.store.peekAll(
          'userhotelrelationship',
        ) as unknown as Userhotelrelationship[]
      ).find((relationship) => relationship.subscriptionId === subscriptionId);

      if (record) {
        this.store.unloadRecord(record);
      }

      this.relationships = this.relationships.filter(
        (relationship) => relationship.subscriptionId !== subscriptionId,
      );
      this.totalResults = Math.max(0, this.totalResults - 1);

      this.toast.success(
        this.intl.t('settings.users.subscription_access.delete_success'),
        { showIcon: true },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.subscription_access.delete_failure'),
        { showIcon: true },
      );
      await this.reload();
    }
  }

  @action
  onLoadMore(): void {
    void this.loadMoreTask.perform();
  }

  @action
  onSearchChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchText);
  }

  @action
  onSortChange(sorts: SortState[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    }

    void this.reload();
  }

  @action
  async saveChanges(changes: SubscriptionAccessChange[]): Promise<boolean> {
    if (this.isOwnUser) {
      const confirmed = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.hotel_access.confirm_edit_self'),
      });

      if (!confirmed) {
        return false;
      }
    }

    const changedSubscriptionCount = changes.reduce(
      (total, change) => total + change.subscriptionIds.length,
      0,
    );

    try {
      for (const change of changes) {
        await this.ajaxService.request('userhotelrelationships/', {
          method: 'PUT',
          data: {
            subscription_ids: change.subscriptionIds,
            user_id: change.userId,
            ...(change.accessRight && { access_right: change.accessRight }),
            ...(change.accessibleProducts && {
              accessible_products: change.accessibleProducts,
            }),
          },
        });
      }

      this.toast.success(
        this.intl.t('settings.users.edit.success', {
          count: changedSubscriptionCount,
        }),
        { showIcon: true },
      );

      await this.reload();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', {
          count: changedSubscriptionCount,
        }),
        { showIcon: true },
      );
      await this.reload();
      return false;
    }
  }
}
